import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  Grid,
  MenuItem,
} from '@mui/material';
import {
  DarkGreenButton,
  Select,
  TextButton,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { Birthdate } from '../../../components/v2/styled/Birthdate';
import { ErrorBox } from '../../../components/v2/styled/ErrorBox';
import { ZichtAccountDutch } from '../../../components/v2/styled/ZichtAccountDutch';
import { DropzoneArea } from 'mui-file-dropzone';

import { User } from '../../../models/User';

var he = require('he');
const { DateTime } = require("luxon");

type Props = {
  setStep: (n:number) => void;
  user?: User;
  setUser: (u:User) => void;
  onSubmit: () => void;
  loading?: boolean;
}

export const EmissionChoiceMeDutch: FunctionComponent<Props> = ({setStep, user, setUser, onSubmit, loading}) => {
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const [isOkay, setIsOkay] = useState<boolean>(false);
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const isValidDutchZip = (zip: string) => {
    return /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/.test(zip.trim());
  };

  const isValidDutchPhone = (phone: string) => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    return /^((\+31)|(0031)|0)[1-9][0-9]{8}$/.test(cleanPhone);
  };

  const isBirthdateInvalid = !user?.birth_date || !DateTime.fromFormat(user.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18});

  const gender = user?.gender || 'M';

  const isFormValid = 
    !!user?.voorletters &&
    !!user?.first_name && 
    !!user.last_name && 
    !!gender && 
    !!user.phone && 
    // isValidDutchPhone(user.phone) && 
    !!user.zicht_account && 
    isOkay && 
    !!user.street && 
    !!user.number && 
    !!user.zip && 
    !!user.country && 
    (user.country !== "NL" || (user.country === "NL" && isValidDutchZip(user.zip))) && 
    !!user.city && 
    !!user.birth_date && 
    !!user.identity && 
    user?.email === user.repeat_email && 
    !isBirthdateInvalid;

  return <>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_personal")}</Typography>
    </Grid>
    <Grid item xs={3}>
      <TextInput
        value={user?.voorletters || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            voorletters: v.toString().toUpperCase()
          });
        }}
        starred
        caption={t("nw.nl.user_info.voorletters")}
        isError={showErrors && !user?.voorletters}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextInput
        value={user?.first_name || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            first_name: v.toString()
          });
        }}
        starred
        caption={t("nw.nl.user_info.first_name")}
        isError={showErrors && !user?.first_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={5}>
      <TextInput
        value={user?.last_name || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            last_name: v.toString()
          });
        }}
        starred
        caption={t("nw.user_info.last_name")}
        isError={showErrors && !user?.last_name}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={7}>
      <Birthdate
        value={user?.birth_date || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            birth_date: v.target.value
          });
        }}
        starred
        caption={t("nw.user_info.birth_date")}
        isError={(!!user?.birth_date || showErrors) && isBirthdateInvalid}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={5}>
      <Select
        id="subjects"
        caption={t("nw.user_info.gender")}
        fullWidth
        value={user?.gender || "M"}
        setValue={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            gender: v.toString()
          });
        }}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          height: '40px',
          paddingTop: 0.25,
          paddingBottom: 0.25,
          backgroundColor: 'transparent',
          "&:hover fieldset": {
            borderColor: 'transparent !important'
          }
        }}
      >
        <MenuItem value="M">{he.decode((t("nw.user_info.genders.male")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="V">{he.decode((t("nw.user_info.genders.female")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
      </Select>
    </Grid>
    {!!user?.birth_date && DateTime.fromFormat(user?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18}) && <Grid item xs={12}>
      <ErrorBox
        type="error"
        noTitle
        text={t("nw.emission.choice_me_new_itsme.error_age")}
      />
    </Grid>}
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="subtitle1" sx={{
        display: 'inline-block',
        fontStyle: 'italic'
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra_email")}</Typography>
    </Grid>
    <Grid item xs={12}>
      <TextInput
        value={user?.email || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            email: v?.toString().trim()
          });
        }}
        starred
        caption={t("nw.user_info.email")}
        isError={(showErrors && (!user?.email || (!!user?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user?.email))) || (!!user?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user?.email)))}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        value={user?.repeat_email || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            repeat_email: v
          });
        }}
        starred
        caption={t("nw.user_info.repeat_email")}
        isError={showErrors ? (user?.email !== user?.repeat_email) : (!!user?.repeat_email && user?.email !== user?.repeat_email)}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        value={user?.phone || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            phone: v.toString()
          });
        }}
        starred
        caption={t("nw.nl.user_info.phone")}
        placeholder="0031 6 12345678"
        isError={showErrors && !user?.phone}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_address")}</Typography>
    </Grid>
    <Grid item xs={8}>
      <TextInput
        value={user?.street || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            street: v
          });
        }}
        starred
        caption={t("nw.user_info.street")}
        isError={showErrors && !user?.street}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextInput
        value={user?.number || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            number: v
          });
        }}
        starred
        caption={t("nw.nl.user_info.number")}
        isError={showErrors && !user?.number}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={3}>
      <TextInput
        value={user?.zip || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            zip: v
          });
        }}
        starred
        caption={t("nw.nl.user_info.zip")}
        isError={showErrors ? (!user?.zip || (user.country === "NL" && !isValidDutchZip(user.zip))) : (!!user?.zip && user.country === "NL" && !isValidDutchZip(user.zip))}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={6}>
      <TextInput
        value={user?.city || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            city: v
          });
        }}
        starred
        caption={t("nw.user_info.city")}
        isError={showErrors && !user?.city}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={3}>
      <Select
        id="country"
        caption={t("nw.user_info.country")}
        fullWidth
        value={user?.country || "NL"}
        setValue={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            country: v.toString()
          });
        }}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          height: '40px',
          paddingTop: 0.25,
          paddingBottom: 0.25,
          backgroundColor: 'transparent',
          "&:hover fieldset": {
            borderColor: 'transparent !important'
          }
        }}
      >
        <MenuItem value="NL">{he.decode((t("nw.user_info.countries.nl")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="ARU">{he.decode((t("nw.user_info.countries.nl_aruba")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="SMA">{he.decode((t("nw.user_info.countries.nl_sintmaarten")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="CUR">{he.decode((t("nw.user_info.countries.nl_curacao")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
        <MenuItem value="OTHR">{he.decode((t("nw.user_info.countries.nl_other")).replace(/<[^>]*>?/gm, ''))}</MenuItem>
      </Select>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_banking")}</Typography>
    </Grid>
    <Grid item xs={12}>
      <ZichtAccountDutch
        value={user?.zicht_account || ''}
        onChange={(v) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            zicht_account: v.target.value
          });
        }}
        setExternalIsOkay={setIsOkay}
        starred
        caption={t("nw.nl.user_info.zicht_account")}
        error={showErrors && (!user?.zicht_account || !isOkay) ? t("nw.general_error.invalid_iban") : undefined}
        sx={{
          borderRadius: "8px",
          width: '100%',
          border: "2px solid transparent",
          borderColor: theme.palette.primary.dark,
          paddingTop: 0.25,
          paddingBottom: 0.25,
          height: '40px',
          backgroundColor: 'transparent',
        }}
      />
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="subtitle1" sx={{
        display: 'inline-block',
        fontStyle: 'italic'
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short_extra_subline")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{
      display: 'flex',
      "& .cust_dropzonearea > div > div": {
        flexBasis: 'auto !important',
        marginLeft: 'auto',
        marginRight: 'auto'
      }
    }}>
      <DropzoneArea
        acceptedFiles={['image/gif', 'image/jpeg', 'image/png', '.pdf']}
        filesLimit={1}
        fileObjects={[]}
        dropzoneText={he.decode(t('nw.general.dropzone_text').replace(/<[^>]*>?/gm, ''))}
        getDropRejectMessage={(rejectedFile, x, y) => {
          return he.decode(t("nw.general_error.dropzone.rejected", {name: rejectedFile.name}).replace(/<[^>]*>?/gm, ''));
        }}
        onChange={(files) => {
          setUser({
            ...(user || {
              auth_token: '',
              email: ''
            }),
            identity: files[0]
          });
        }}
        maxFileSize={3000000}
        previewGridProps={{item: {style: {maxWidth: '100%', flexBasis: '100%'}}}}
        dropzoneClass="cust_dropzonearea"
      />
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'center'}}>
      <Typography variant="h3" sx={{
        marginTop: 4,
        display: 'inline-block',
      }}>{t("nw.emission.choice_me_new_itsme.title_overview")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.voorletters || !user?.first_name || !user.last_name || !user.birth_date || !gender || !DateTime.fromFormat(user?.birth_date, "dd/LL/y").isValid || DateTime.fromFormat(user?.birth_date, "dd/LL/y") > DateTime.now().minus({year: 18})) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_personal")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!( !user?.phone || user?.email !== user.repeat_email) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!( !user?.phone || user?.email !== user.repeat_email) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: ( !user?.phone || user?.email !== user.repeat_email) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_contact_extra")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!( !user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!( !user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: ( !user?.street || !user.number || !user.zip || (user.country === "NL" && !isValidDutchZip(user.zip)) || !user.city) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_address")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.zicht_account || !isOkay) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.zicht_account || !isOkay) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.zicht_account || !isOkay) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.emission.choice_me_new_itsme.title_banking")}</Typography>
    </Grid>
    <Grid item xs={12} sx={{textAlign: 'left'}}>
      {!(!user?.identity) && <CheckCircleOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.primary.light}} />}
      {!!(!user?.identity) && <ErrorOutlineIcon sx={{marginRight: 2, marginBottom: -1, color: theme.palette.warning.main}} />}
      <Typography variant="subtitle1" sx={{
        marginTop: 1,
        display: 'inline-block',
        color: (!user?.identity) ? theme.palette.warning.main : theme.palette.primary.light,
      }}>{t("nw.nl.emission.choice_me_new_itsme.title_identity_short")}</Typography>
    </Grid>

    <Grid item xs={12} sx={{textAlign: 'center', paddingTop: 4, paddingRight: {xs: 0, md: 2.5}}}>
      <TextButton
        id="back_btn"
        label={t("nw.emission.forgot.cta_back")}
        onClick={() => {
          setStep(5);
          window.scrollTo(0, 0);
        }}
        sx={{
          marginLeft: 1,
          marginRight: 1,
          marginTop: 1
        }}
      />
      <DarkGreenButton
        id="next_btn"
        label={t("nw.emission.choice_me_new_itsme.cta_next")}
        loading={!!loading}
        onClick={() => {
          if (isFormValid) {
            onSubmit();
          } else {
            setShowErrors(true);
            window.scrollTo(0, 0);
          }
        }}
        sx={{
          marginLeft: 1,
          marginRight: 1,
          marginTop: 1,
          color: 'white !important'
        }}
      />
    </Grid>
  </>;
}
